/**
 * DashboardScreen.tsx — Authenticated dashboard (task H01).
 *
 * Shows the user's game memberships (role, status, end date, budget) with
 * an Open Game action per row, the `activeCreatedGameCount / limit` line,
 * and the two entry points: `Create a new game` (Setup Game overlay, H02)
 * and `Join a new game` (Join Game overlay, H03).
 *
 * The state is always fetched from the server (source of truth) — the
 * backend enforces the 5-active-created-games limit; the count line is
 * display-only.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiClientError, type GameListItem, type UserDto } from '@shared';
import { apiV1 } from '../api';
import { formatBudgetCents } from '../format';
import { JoinGameOverlay } from '../overlays/JoinGameOverlay';
import { SetupGameOverlay } from '../overlays/SetupGameOverlay';

interface DashboardScreenProps {
  user: UserDto;
  onOpenGame: (gameId: number) => void;
  onLogout: () => void;
}

export type DashboardOverlay = 'create' | 'join' | null;

export function DashboardScreen({ user, onOpenGame, onLogout }: DashboardScreenProps) {
  const [games, setGames] = useState<GameListItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCreatedGameCount, setActiveCreatedGameCount] = useState<number | null>(null);
  const [activeCreatedGameLimit, setActiveCreatedGameLimit] = useState<number | null>(null);
  // H02: Setup Game overlay; H03: Join Game overlay.
  const [activeOverlay, setActiveOverlay] = useState<DashboardOverlay>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiV1.listGames();
      setGames(data.games);
      setActiveCreatedGameCount(data.activeCreatedGameCount);
      setActiveCreatedGameLimit(data.activeCreatedGameLimit);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not load your games.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <main className="app-shell dashboard">
      <header className="dashboard-header">
        {/* Title + session identity: top-LEFT corner. */}
        <div className="dashboard-header-title">
          <h1>Secret Santa</h1>
          <p className="muted">
            Logged in as <strong>{user.username}</strong>
          </p>
          {activeCreatedGameCount !== null && activeCreatedGameLimit !== null && (
            <span className="created-count">
              {activeCreatedGameCount} / {activeCreatedGameLimit} active created games
            </span>
          )}
        </div>
        {/* Log out: top-RIGHT corner (floating). */}
        <button type="button" className="game-back dashboard-logout" onClick={onLogout}>
          Log out
        </button>
      </header>

      <div className="dashboard-actions">
        <button type="button" className="game-refresh" onClick={() => setActiveOverlay('join')}>
          Join a new game
        </button>
      </div>

      {/* Floating action button (bottom-right): the only create entry point. */}
      <button
        type="button"
        className="fab"
        aria-label="Create a new game"
        onClick={() => setActiveOverlay('create')}
      >
        +
      </button>

      {games === null && loading && <p className="dashboard-status" aria-live="polite">Loading your games…</p>}

      {games === null && !loading && (
        <>
          <p className="dashboard-status" role="alert">
            {error}
          </p>
          <button type="button" className="game-refresh" onClick={() => void refresh()}>
            Retry
          </button>
        </>
      )}

      {games !== null && games.length === 0 && (
        <p className="dashboard-status">You are not a member of any games yet. Create one, or join with an invite code.</p>
      )}

      {games !== null && games.length > 0 && (
        <ul className="dashboard-game-list">
          {games.map((game) => (
            <li key={game.id} className="dashboard-game-row">
              <div className="dashboard-game-info">
                <span className="dashboard-game-id">{game.name || `Game #${game.id}`}</span>
                <span className={`badge badge--${game.role.toLowerCase()}`}>{game.role}</span>
                <span className={`badge badge--${game.status.toLowerCase()}`}>{game.status}</span>
                <span className="muted">
                  {formatBudgetCents(game.giftBudgetCents)} budget · ends {game.endDate}
                </span>
              </div>
              <button type="button" className="auth-submit" onClick={() => onOpenGame(game.id)}>
                Open Game
              </button>
            </li>
          ))}
        </ul>
      )}

      {activeOverlay === 'create' && (
        <SetupGameOverlay
          onClose={() => setActiveOverlay(null)}
          onCreated={(game) => {
            setActiveOverlay(null);
            onOpenGame(game.id);
          }}
        />
      )}
      {activeOverlay === 'join' && (
        <JoinGameOverlay
          onClose={() => setActiveOverlay(null)}
          onJoined={(game) => {
            setActiveOverlay(null);
            onOpenGame(game.id);
          }}
        />
      )}
    </main>
  );
}
